/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// uuid is a library for generating unique id
import { v4 as uuidv4 } from "uuid";

// @mui material components
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI base styles
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";

function Table({ columns, rows }) {
  // console.log("columns"+columns);
  // console.log(rows);

  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  const renderColumns = columns.map(
    ({ name, label, align, width, headerColor, headerOpacity, headerFontWeight }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columns.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <ArgonBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={headerFontWeight || fontWeightBold}
        color={headerColor || "#7686a6ff"}
        opacity={headerOpacity ?? 0.7}
        sx={({ palette: { light } }) => ({ borderBottom: `${borderWidth[1]} solid ${light.main}` })}
      >
        {typeof label === "string"
          ? label.toUpperCase()
          : label
          ? label
          : typeof name === "string"
          ? name.toUpperCase()
          : name}
      </ArgonBox>
    );
    }
  );

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;

    const tableRow = columns.map(({ name, align }) => {
      // console.log(name);
      let template;

      if (Array.isArray(row[name])) {
        template = (
          <ArgonBox
            key={uuidv4()}
            component="td"
            p={1}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <ArgonBox display="flex" alignItems="center" py={0.5} px={1}>
              <ArgonBox mr={2}>
                <ArgonAvatar src={row[name][0]} name={row[name][1]} variant="rounded" size="md" />
              </ArgonBox>
              <ArgonTypography variant="button" fontWeight="medium" sx={{ width: "max-content" }}>
                {row[name][1]}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        );
      } else {
        template = (
          <ArgonBox
            key={uuidv4()}
            component="td"
            p={1}
            textAlign={align}
            verticalAlign="middle"
            lineHeight={0.65}
            sx={({ palette: { light } }) => ({
              borderBottom: row.hasBorder ? `${borderWidth[1]} solid ${light.main}` : null,
            })}
          >
            <ArgonTypography
              variant="button"
              fontWeight="regular"
              sx={{
                fontSize: "13px",
                display: "inline-block",
                width: "max-content",
                color: "#344767",
                textTransform: "none",
              }}
            >
              {row[name]}
            </ArgonTypography>
          </ArgonBox>
        );
      }

      return template;
    });

    return (
      <TableRow key={rowKey} sx={row.rowSx || {}}>
        {tableRow}
      </TableRow>
    );
  });

  return useMemo(
    () => (
      <TableContainer
        sx={{
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: "4px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#b8b5b5ff", borderRadius: "4px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1", borderRadius: "4px" },
        }}
      >
        <MuiTable>
          <ArgonBox component="thead">
            <TableRow>{renderColumns}</TableRow>
          </ArgonBox>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
      </TableContainer>
    ),
    [columns, rows]
  );
}

// Setting default values for the props of Table
Table.defaultProps = {
  columns: [],
  rows: [{}],
};

// Typechecking props for the Table
Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default Table;
